import { styled as muiStyled, css } from '@mui/material/styles';
import React from 'react';

import { ProjectData } from './index';

import { spc } from '~/client/components/base/spacer';
import { usePermissions } from '~/client/contexts/permissions-context';
import { useDashboardComponentsConfig } from '~/client/hooks/use-dashboard-components-config';
import { aiDashboardUtils, dashboardRouteUtil } from '~/client/routes';
import * as GQL from '~/graphql/generated/client';
import { convertAssessmentCategoryStatus } from '~c/components/assessment/helper';
import ProjectTable from '~c/components/project-table';

interface Props {
  className?: string;
  projectListData: ProjectData[];
  page?: number;
  onPageChange?: (page: number) => void;
  rowsPerPage?: number;
  onRowsPerPageChange?: (pageSize: number) => void;
  totalCount?: number;
  sortDirection?: GQL.SortDirection | null;
  onSortChange?: (direction: GQL.SortDirection | null) => void;
  riskFilter?: GQL.RiskFilter;
  onRiskSortChange?: (filter: GQL.RiskFilter) => void;
  tabsConfig?: {
    tabs: { label: string; value: string }[];
    activeTab: string;
    onTabChange: (tab: string) => void;
    alternateTabContent?: React.ReactNode;
    alternateTabAction?: React.ReactNode;
  };
  searchSlot?: React.ReactNode;
}

const ProjectList: React.FC<Props> = ({
  className,
  projectListData,
  page,
  onPageChange,
  rowsPerPage,
  onRowsPerPageChange,
  totalCount,
  sortDirection,
  onSortChange,
  riskFilter,
  onRiskSortChange,
  tabsConfig,
  searchSlot,
}) => {
  const { navigateTo } = aiDashboardUtils.useHelper();
  const { navigateTo: navigateToDashboard } = dashboardRouteUtil.useHelper();
  const { visibility } = useDashboardComponentsConfig();
  const showTrueColumns = visibility.showProjectTableTrueColumns;
  const { hasCapability } = usePermissions();
  const canCreateProject = hasCapability('org:create_project');

  return (
    <div className={className}>
      <ProjectTable
        title=""
        addButtonText={canCreateProject ? 'Add Project' : undefined}
        onAddButtonClick={
          canCreateProject
            ? () => navigateToDashboard('CREATE_AI_SYSTEM')
            : undefined
        }
        data={projectListData}
        projectName={(entry) => entry.projectName}
        source={(entry) => entry.source}
        risk={(entry) => entry.risk}
        assessmentCompletion={
          showTrueColumns ? undefined : (entry) => entry.assessmentCompletion
        }
        policyAlignment={(entry) =>
          entry.policyAlignment === GQL.AssessmentRiskStatusType.Low
        }
        securityStatus={(entry) =>
          convertAssessmentCategoryStatus(entry.securityStatus)
        }
        privacyStatus={(entry) =>
          convertAssessmentCategoryStatus(entry.privacyStatus)
        }
        biasStatus={(entry) =>
          convertAssessmentCategoryStatus(entry.biasStatus)
        }
        performanceStatus={(entry) =>
          convertAssessmentCategoryStatus(entry.performanceStatus)
        }
        showTrueAssessmentStatusColumns={showTrueColumns}
        assessmentStatusByPolicy={(entry) =>
          entry.assessmentStatusByPolicy ?? []
        }
        projectStatus={(entry) => entry.risk}
        isDraft={(entry) => entry.isDraft === true}
        onClick={(entry) => {
          // A draft project reopens the creation stepper (pre-filled) instead
          // of the dashboard.
          if (entry.isDraft) {
            navigateToDashboard(
              'CREATE_AI_SYSTEM',
              undefined,
              `?projectId=${entry.id}`
            );
            return;
          }
          navigateTo('DASHBOARD', {
            params: { projectId: entry.id },
          });
        }}
        page={page ?? 0}
        onPageChange={onPageChange ?? (() => {})}
        rowsPerPage={rowsPerPage ?? 10}
        onRowsPerPageChange={onRowsPerPageChange ?? (() => {})}
        totalCount={totalCount ?? 0}
        sortDirection={sortDirection}
        onSortChange={onSortChange}
        riskFilter={riskFilter}
        onRiskSortChange={onRiskSortChange}
        storageKey="projects-view"
        owner={(entry) => {
          const ownerMember = entry.sharedWith?.find(
            (m: any) => m.role === 'Owner'
          );
          return ownerMember
            ? `${ownerMember.firstName ?? ''} ${ownerMember.lastName ?? ''}`.trim() ||
                ownerMember.email
            : undefined;
        }}
        showLegend
        tabsConfig={tabsConfig}
        searchSlot={searchSlot}
      />
    </div>
  );
};

export default muiStyled(ProjectList)(
  ({ theme }) => css`
    /* Add responsive spacing for small screens */
    @media (max-width: 768px) {
      .project-table {
        gap: ${theme.spacing(spc.between_vertical_s)}
          ${theme.spacing(spc.between_horizontal_s)} !important;
        overflow-x: auto;
      }
    }
  `
);
